import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldX, Info, Eye, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatCard } from "@/components/common/StatCard";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SEM = {
  Verde: { label: "Sin riesgo", cls: "bg-emerald-100 text-emerald-700" },
  Ambar: { label: "Atención", cls: "bg-amber-100 text-amber-700" },
  Rojo: { label: "En riesgo", cls: "bg-red-100 text-red-700" },
};

function SemBadge({ semaforo }) {
  const m = SEM[semaforo] || SEM.Verde;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.cls}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {m.label}
    </span>
  );
}

function LeyendaSemaforo() {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button data-testid="semaforo-leyenda-trigger" className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-primary">
            <Info className="h-3.5 w-3.5" /> ¿Cómo se calcula el semáforo?
          </button>
        </TooltipTrigger>
        <TooltipContent data-testid="semaforo-leyenda" className="max-w-xs text-left">
          <p className="mb-1 font-semibold">Semáforo de riesgo regulatorio</p>
          <ul className="space-y-1 text-xs">
            <li><span className="font-semibold text-red-300">Rojo</span>: más de 2 obligaciones vencidas o pago vencido.</li>
            <li><span className="font-semibold text-amber-300">Ámbar</span>: 1–2 obligaciones vencidas o reportes próximos a vencer.</li>
            <li><span className="font-semibold text-emerald-300">Verde</span>: sin obligaciones vencidas ni pagos vencidos.</li>
          </ul>
          <p className="mt-2 text-[11px] text-slate-300">El estado técnico de las APIs (producción/demo) NO afecta el color: es solo informativo.</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function RiesgoPlataforma() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (user && user.role !== "super_admin") { setError(true); setLoading(false); return; }
    if (user) {
      api.get("/admin/riesgo-plataforma")
        .then((r) => setData(r.data))
        .catch(() => setError(true))
        .finally(() => setLoading(false));
    }
  }, [user]);

  if (error)
    return (
      <div>
        <PageHeader title="Tablero de riesgo" subtitle="Panel de plataforma" />
        <div className="flex flex-col items-center justify-center gap-3 p-16 text-slate-400">
          <ShieldX className="h-10 w-10" />
          <p className="text-sm">Acceso exclusivo del Super Administrador de la plataforma.</p>
        </div>
      </div>
    );

  const t = data?.totales || {};
  const goFicha = (row) => navigate(`/entidades/${row.id}`);

  return (
    <div data-testid="riesgo-plataforma">
      <PageHeader
        title="Tablero de riesgo agregado"
        subtitle="Riesgo regulatorio y de cumplimiento por entidad (agregado, sin datos operativos)"
      >
        <LeyendaSemaforo />
      </PageHeader>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : data ? (
        <div className="space-y-6 p-6 md:p-8">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard testid="kpi-en-riesgo" label="Entidades en riesgo" value={t.en_rojo ?? 0}
              icon="ShieldAlert" accent="red" hint={`${t.en_ambar ?? 0} en atención · ${t.en_verde ?? 0} al día`} />
            <StatCard testid="kpi-reportes-vencidos" label="Reportes vencidos (plataforma)" value={t.reportes_vencidos ?? 0}
              icon="CalendarX" accent="red" hint={`${t.reportes_proximos ?? 0} por vencer`} />
            <StatCard testid="kpi-alertas-pld" label="Alertas PLD abiertas" value={t.alertas_pld_abiertas ?? 0}
              icon="Siren" accent="amber" hint="Conteo agregado, sin detalle" />
            <StatCard testid="kpi-pago-vencido" label="Entidades con pago vencido" value={t.entidades_pago_vencido ?? 0}
              icon="CreditCard" accent="primary" hint={`${t.entidades_api_sin_produccion ?? 0} con API en demo`} />
          </div>

          {/* Distribución de semáforo */}
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="distribucion-semaforo">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Distribución de riesgo ({t.entidades ?? 0} entidades)</p>
            <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100">
              {["Rojo", "Ambar", "Verde"].map((k) => {
                const key = { Rojo: "en_rojo", Ambar: "en_ambar", Verde: "en_verde" }[k];
                const n = t[key] ?? 0;
                const pct = (t.entidades || 0) > 0 ? (n / t.entidades) * 100 : 0;
                const bg = { Rojo: "bg-red-500", Ambar: "bg-amber-500", Verde: "bg-emerald-500" }[k];
                return <div key={k} className={bg} style={{ width: `${pct}%` }} title={`${SEM[k].label}: ${n}`} />;
              })}
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-600">
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-500" /> En riesgo: <b>{t.en_rojo ?? 0}</b></span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500" /> Atención: <b>{t.en_ambar ?? 0}</b></span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Al día: <b>{t.en_verde ?? 0}</b></span>
            </div>
          </div>

          {/* Tabla por entidad */}
          <DataTable
            testid="table-riesgo-plataforma"
            rows={data.entidades || []}
            onRowClick={goFicha}
            columns={[
              { key: "nombre_comercial", label: "Entidad" },
              { key: "regimen", label: "Régimen", kind: "badge" },
              { key: "semaforo_riesgo", label: "Riesgo", render: (r) => <SemBadge semaforo={r.semaforo_riesgo} /> },
              { key: "obligaciones_vencidas", label: "Reportes vencidos", render: (r) => <span className={`font-semibold tabular-nums ${r.obligaciones_vencidas > 0 ? "text-red-600" : "text-slate-700"}`}>{r.obligaciones_vencidas}</span> },
              { key: "reportes_proximos", label: "Por vencer", render: (r) => <span className="tabular-nums text-amber-600">{r.reportes_proximos}</span> },
              { key: "alertas_abiertas", label: "Alertas PLD", render: (r) => <span className="tabular-nums">{r.alertas_abiertas}</span> },
              { key: "estatus_pago", label: "Pago", kind: "badge" },
              { key: "api_produccion", label: "API", render: (r) => (
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${r.api_produccion ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                  {r.api_produccion ? "Producción" : "Demo"}
                </span>
              ) },
            ]}
            actions={(r) => (
              <Button data-testid={`riesgo-ver-${r.id}`} size="sm" variant="outline" className="h-8 gap-1.5 px-2.5 text-primary" onClick={() => goFicha(r)}>
                <Eye className="h-4 w-4" /> Ver ficha
              </Button>
            )}
            emptyLabel="Sin entidades registradas"
          />
        </div>
      ) : null}
    </div>
  );
}
